/**
 * Self-contained sign-in / sign-up form.
 *
 * Factored out of SignInDialog so it can be rendered inline (e.g. inside the
 * TableScanInterstitial) without the modal chrome. State is local — no
 * dependency on SignInDialogContext.
 *
 * Pulls auth methods from AuthActionsContext (same path the dialog uses), so
 * the AuthProvider's onAuthStateChanged side effects (login-event ping,
 * profile fetch, etc.) still fire normally.
 */

import { useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { AuthActionsContext, useAuth } from "../lib/platform/AuthProvider";
import { useT } from "../lib/i18n";

export type SignInFormView =
  | "main"
  | "signin-password"
  | "signup-password"
  | "reset-password"
  | "account-not-found";
export type AuthProvider = "apple" | "google" | "email";

interface SignInFormProps {
  /** Controlled view — when supplied alongside `onViewChange`, view state is
   *  driven from the parent (used by SignInDialog so AuthProvider can flip
   *  the form to "account-not-found" via context). Otherwise the form keeps
   *  its own view state seeded from `initialView`. */
  view?: SignInFormView;
  onViewChange?: (next: SignInFormView) => void;
  /** Initial view in uncontrolled mode. Defaults to "main". */
  initialView?: SignInFormView;
  /**
   * Whether the email path is sign-up (collects name + confirm password) or
   * sign-in (email + password). Ignored when the view is forced to a
   * specific password view via `view` / `initialView`.
   */
  asSignUp?: boolean;
  /** Optional title above the form. Omit for a chromeless embed. */
  title?: string;
  /** Optional subtitle. */
  subtitle?: string;
  /** Allow the user to toggle between sign-up and sign-in inside the form. */
  allowSwitch?: boolean;
  /** Called after a successful sign-in or sign-up returns a user. */
  onSuccess?: (provider: AuthProvider) => void;
  /** Fired when the user taps any auth button — useful for tracking. */
  onAuthClick?: (provider: AuthProvider) => void;
  /** Fired when an OAuth attempt resolves without a user (cancel/failure). */
  onAuthFail?: (provider: AuthProvider) => void;
  /** Account-not-found "Create profile" tap — typically closes the parent
   *  surface so ProtectedRoute can redirect to /quiz. */
  onAccountNotFoundCreate?: () => void;
  /** Optional outer container class. */
  className?: string;
}

function AppleLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function GoogleLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

export default function SignInForm({
  view: controlledView,
  onViewChange,
  initialView = "main",
  asSignUp = true,
  title,
  subtitle,
  allowSwitch = true,
  onSuccess,
  onAuthClick,
  onAuthFail,
  onAccountNotFoundCreate,
  className,
}: SignInFormProps) {
  const { t } = useT();
  const actions = useContext(AuthActionsContext);
  const { signOut } = useAuth();

  const [internalView, setInternalView] = useState<SignInFormView>(initialView);
  const view = controlledView ?? internalView;
  const setView = (next: SignInFormView) => {
    if (onViewChange) onViewChange(next);
    if (controlledView === undefined) setInternalView(next);
  };

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [oauthPending, setOauthPending] = useState<"apple" | "google" | null>(null);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Firebase's `signInWithPopup` doesn't always reject when the user closes
  // the popup manually (depends on browser, cookie policy, popup blocker).
  // Without this, the spinner can stay on the button forever after a cancel.
  // Watchdog: while OAuth is pending, listen for the parent window regaining
  // focus (i.e. the popup just closed). After a short grace period — to let
  // Firebase resolve naturally if it's going to — clear the pending state.
  useEffect(() => {
    if (!oauthPending) return;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const onFocus = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setOauthPending(null), 500);
    };
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [oauthPending]);

  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  if (!actions) {
    console.warn("[SignInForm] No AuthActionsContext — auth methods unavailable.");
    return null;
  }

  const {
    signInWithApple,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    isLoading,
    error,
    clearError,
  } = actions;

  const handleApple = async () => {
    if (oauthPending) return;
    onAuthClick?.("apple");
    setOauthPending("apple");
    try {
      const user = await signInWithApple();
      if (user) onSuccess?.("apple");
      else onAuthFail?.("apple");
    } finally {
      setOauthPending(null);
    }
  };

  const handleGoogle = async () => {
    if (oauthPending) return;
    onAuthClick?.("google");
    setOauthPending("google");
    try {
      const user = await signInWithGoogle();
      if (user) onSuccess?.("google");
      else onAuthFail?.("google");
    } finally {
      setOauthPending(null);
    }
  };

  const handleEmailEntry = () => {
    onAuthClick?.("email");
    setView(asSignUp ? "signup-password" : "signin-password");
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    clearAllErrors();
    if (!email.trim()) return setEmailError(t("auth.errors.enterEmail"));
    if (!isValidEmail(email)) return setEmailError(t("auth.errors.invalidEmail"));
    await resetPassword(email);
    setResetEmailSent(true);
  };

  // Account-not-found "Try other account" — sign user out, reset local form
  // state, drop them back to the welcome view so they can pick a new flow.
  const handleAccountNotFoundTryOther = async () => {
    await signOut();
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setFirstName("");
    setLastName("");
    setResetEmailSent(false);
    clearAllErrors();
    setView("main");
  };

  const clearAllErrors = () => {
    setEmailError(null);
    setPasswordError(null);
    setConfirmPasswordError(null);
    setNameError(null);
    clearError();
  };

  const switchView = (next: SignInFormView) => {
    setView(next);
    setPassword("");
    setConfirmPassword("");
    setFirstName("");
    setLastName("");
    clearAllErrors();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearAllErrors();

    if (!email.trim()) return setEmailError(t("auth.errors.enterEmail"));
    if (!isValidEmail(email)) return setEmailError(t("auth.errors.invalidEmail"));
    if (!password.trim()) return setPasswordError(t("auth.errors.enterPassword"));

    if (view === "signup-password") {
      if (!firstName.trim()) return setNameError(t("auth.errors.enterFirstName"));
      if (password.length < 6) return setPasswordError(t("auth.errors.passwordMinLength"));
      if (password !== confirmPassword) {
        return setConfirmPasswordError(t("auth.errors.passwordMismatch"));
      }
      const user = await signUpWithEmail(email, password, firstName, lastName);
      if (user) onSuccess?.("email");
    } else {
      const user = await signInWithEmail(email, password);
      if (user) onSuccess?.("email");
    }
  };

  return (
    <div className={className}>
      {(title || subtitle) && (
        <div className="text-center mb-4">
          {title && (
            <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-50 mb-1">{title}</h3>
          )}
          {subtitle && (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">{subtitle}</p>
          )}
        </div>
      )}

      <AnimatePresence mode="wait">
        {view === "main" ? (
          <motion.div
            key="main"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.18 }}
            className="flex flex-col gap-2.5"
          >
            <button
              type="button"
              onClick={handleApple}
              disabled={!!oauthPending}
              className="w-full py-3.5 rounded-full font-semibold text-[15px] flex items-center justify-center gap-2.5 transition-all active:scale-[0.99] disabled:opacity-60"
              style={{
                background: "#ffffff",
                color: "#000000",
                boxShadow:
                  "0 0 0 1px rgba(255,255,255,0.4), 0 8px 20px rgba(0,0,0,0.4)",
              }}
            >
              {oauthPending === "apple" ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <AppleLogo className="w-5 h-5" />
              )}
              {asSignUp
                ? t("auth.signUp.withApple")
                : t("auth.signIn.withApple")}
            </button>

            <button
              type="button"
              onClick={handleGoogle}
              disabled={!!oauthPending}
              className="w-full py-3.5 rounded-full font-semibold text-[15px] flex items-center justify-center gap-2.5 transition-all active:scale-[0.99] disabled:opacity-60"
              style={{
                background: "rgba(0, 0, 0, 0.8)",
                color: "#FEF3EE",
                border: "1px solid rgba(255,255,255,0.12)",
                backdropFilter: "blur(8px)",
              }}
            >
              {oauthPending === "google" ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <GoogleLogo className="w-5 h-5" />
              )}
              {asSignUp
                ? t("auth.signUp.withGoogle")
                : t("auth.signIn.withGoogle")}
            </button>

            <button
              type="button"
              onClick={handleEmailEntry}
              disabled={!!oauthPending}
              className="w-full py-3.5 rounded-full font-semibold text-[15px] flex items-center justify-center gap-2.5 transition-all active:scale-[0.99] disabled:opacity-60"
              style={{
                background: "linear-gradient(135deg, #EF6225 0%, #B868C0 100%)",
                color: "#ffffff",
                boxShadow:
                  "0 0 20px rgba(239,98,37,0.4), 0 0 40px rgba(184,104,192,0.25), inset 0 1px 0 rgba(255,255,255,0.25)",
              }}
            >
              <span className="w-5 h-5 mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280 320-200v-80L480-520 160-720v80l320 200Z"/></svg>
              </span>
              {asSignUp
                ? t("auth.signUp.withEmail")
                : t("auth.signIn.withEmail")}
            </button>

            {error && (
              <p className="mt-2 text-sm text-red-400 text-center">{error}</p>
            )}
          </motion.div>
        ) : view === "signup-password" ? (
          <motion.form
            key="signup"
            name="signup"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.18 }}
            onSubmit={handleSubmit}
            className="space-y-3"
          >
            <BackToMain
              label={t("common.buttons.back")}
              onClick={() => {
                clearAllErrors();
                setView("main");
              }}
            />

            <FormField
              id="signup-email"
              label={t("auth.form.email")}
              type="email"
              autoComplete="email"
              value={email}
              onChange={setEmail}
              placeholder={t("auth.form.emailPlaceholder")}
              error={emailError}
              disabled={isLoading}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                id="signup-firstName"
                label={t("auth.form.firstName")}
                type="text"
                autoComplete="given-name"
                value={firstName}
                onChange={setFirstName}
                placeholder={t("auth.form.firstNamePlaceholder")}
                disabled={isLoading}
                autoFocus
              />
              <FormField
                id="signup-lastName"
                label={t("auth.form.lastName")}
                type="text"
                autoComplete="family-name"
                value={lastName}
                onChange={setLastName}
                placeholder={t("auth.form.lastNamePlaceholder")}
                disabled={isLoading}
              />
            </div>
            {nameError && <p className="text-sm text-red-400">{nameError}</p>}
            <FormField
              id="new-password"
              label={t("auth.form.password")}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={setPassword}
              placeholder={t("auth.form.newPasswordPlaceholder")}
              error={passwordError}
              disabled={isLoading}
            />
            <FormField
              id="confirm-password"
              label={t("auth.form.confirmPassword")}
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder={t("auth.form.confirmPasswordPlaceholder")}
              error={confirmPasswordError}
              disabled={isLoading}
            />
            <SubmitButton
              disabled={isLoading}
              loading={isLoading}
              label={t("auth.signUp.button")}
            />
            {allowSwitch && (
              <p className="text-center text-sm text-neutral-500 dark:text-neutral-400 pt-2">
                {t("auth.signUp.hasAccount")}{" "}
                <button
                  type="button"
                  onClick={() => switchView("signin-password")}
                  className="text-primary-400 hover:underline font-medium"
                >
                  {t("auth.signUp.signInInstead")}
                </button>
              </p>
            )}
          </motion.form>
        ) : view === "reset-password" ? (
          <motion.div
            key="reset"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.18 }}
            className="space-y-3"
          >
            <BackToMain
              label={t("common.buttons.back")}
              onClick={() => {
                setResetEmailSent(false);
                clearAllErrors();
                setView("signin-password");
              }}
            />
            {resetEmailSent ? (
              <div className="text-center space-y-3 pt-2">
                <p className="text-neutral-900 dark:text-neutral-50 font-medium">
                  {t("auth.forgotPassword.emailSent")}
                </p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {t("auth.forgotPassword.emailSentMessage")}{" "}
                  <strong className="text-neutral-700 dark:text-neutral-200">{email}</strong>
                </p>
                <SubmitButton
                  disabled={false}
                  loading={false}
                  label={t("auth.forgotPassword.backToSignIn")}
                  onClick={() => {
                    setResetEmailSent(false);
                    setView("signin-password");
                  }}
                />
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-3">
                <FormField
                  id="reset-email"
                  label={t("auth.form.email")}
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={setEmail}
                  placeholder={t("auth.form.emailPlaceholder")}
                  error={emailError}
                  disabled={isLoading}
                  autoFocus
                />
                <SubmitButton
                  disabled={isLoading}
                  loading={isLoading}
                  label={t("auth.forgotPassword.sendLink")}
                />
              </form>
            )}
          </motion.div>
        ) : view === "account-not-found" ? (
          <motion.div
            key="account-not-found"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.18 }}
            className="space-y-3"
          >
            <button
              type="button"
              onClick={onAccountNotFoundCreate}
              className="w-full py-3 bg-primary-500 hover:bg-primary-400 text-white font-semibold rounded-xl transition-colors"
            >
              {t("auth.accountNotFound.createProfile")}
            </button>
            <button
              type="button"
              onClick={handleAccountNotFoundTryOther}
              className="w-full py-3 bg-neutral-700 hover:bg-neutral-600 text-neutral-900 dark:text-neutral-50 font-semibold rounded-xl transition-colors"
            >
              {t("auth.accountNotFound.tryOtherAccount")}
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="signin"
            name="signin"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.18 }}
            onSubmit={handleSubmit}
            className="space-y-3"
          >
            <BackToMain
              label={t("common.buttons.back")}
              onClick={() => {
                clearAllErrors();
                setView("main");
              }}
            />
            <FormField
              id="signin-email"
              label={t("auth.form.email")}
              type="email"
              autoComplete="email"
              value={email}
              onChange={setEmail}
              placeholder={t("auth.form.emailPlaceholder")}
              error={emailError}
              disabled={isLoading}
              autoFocus
            />
            <FormField
              id="signin-password"
              label={t("auth.form.password")}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
              placeholder={t("auth.form.passwordPlaceholder")}
              error={passwordError}
              disabled={isLoading}
            />
            <SubmitButton
              disabled={isLoading}
              loading={isLoading}
              label={t("auth.signIn.button")}
            />
            <div className="flex justify-between items-center pt-2">
              <button
                type="button"
                onClick={() => {
                  setResetEmailSent(false);
                  clearAllErrors();
                  setView("reset-password");
                }}
                className="text-sm text-neutral-500 dark:text-neutral-400 hover:underline"
              >
                {t("auth.form.forgotPassword")}
              </button>
              {allowSwitch && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {t("auth.signIn.noAccount")}{" "}
                  <button
                    type="button"
                    onClick={() => switchView("signup-password")}
                    className="text-primary-400 hover:underline font-medium"
                  >
                    {t("auth.signIn.createAccount")}
                  </button>
                </p>
              )}
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl"
        >
          <p className="text-sm text-red-400">{error}</p>
        </motion.div>
      )}
    </div>
  );
}

interface FormFieldProps {
  id: string;
  label: string;
  type: "email" | "password" | "text";
  autoComplete: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  error?: string | null;
  disabled?: boolean;
  autoFocus?: boolean;
}

function BackToMain({
  onClick,
  label,
}: {
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 text-[11px] tracking-[0.16em] uppercase font-semibold text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:text-neutral-100 transition-colors mb-2"
    >
      <ArrowLeft className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

function FormField({
  id,
  label,
  type,
  autoComplete,
  value,
  onChange,
  placeholder,
  error,
  disabled,
  autoFocus,
}: FormFieldProps) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="input-label">
        {label}
      </label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        className="input-glass"
      />
      {error && <p className="text-sm text-red-400">{error}</p>}
    </div>
  );
}

function SubmitButton({
  disabled,
  loading,
  label,
  onClick,
}: {
  disabled: boolean;
  loading: boolean;
  label: string;
  /** Optional click handler — when provided, the button switches to type="button"
   *  so it doesn't submit the surrounding form. */
  onClick?: () => void;
}) {
  return (
    <button
      type={onClick ? "button" : "submit"}
      onClick={onClick}
      disabled={disabled}
      className="w-full py-3.5 rounded-full font-semibold text-[15px] flex items-center justify-center gap-2 transition-all active:scale-[0.99] disabled:opacity-60"
      style={{
        background: "linear-gradient(135deg, #EF6225 0%, #B868C0 100%)",
        color: "#ffffff",
        boxShadow:
          "0 0 20px rgba(239,98,37,0.4), 0 0 40px rgba(184,104,192,0.25), inset 0 1px 0 rgba(255,255,255,0.25)",
      }}
    >
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : label}
    </button>
  );
}
